import React, { useCallback, useEffect, useRef, useState } from "react";
import Layout from "../../components/Layout";
import { cancelRun, fetchCurrent, publishRun, startRun } from "./api";
import StartButton from "./components/StartButton";
import ActiveRunPanel from "./components/ActiveRunPanel";
import HistoryList from "./components/HistoryList";
import "./BasecampFranchiseReporting.css";

const POLL_INTERVAL_MS = 3000;

function previousMonthClientSide() {
  // Mirrors the server-side Period.previous_month for the confirmation modal text.
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return {
    year: prev.getFullYear(),
    month: prev.getMonth() + 1,
    month_name: monthNames[prev.getMonth()],
  };
}

export default function BasecampFranchiseReporting() {
  const [state, setState] = useState({
    loading: true,
    hasActiveRun: false,
    job: null,            // current or most-recent
    error: null,
  });
  const pollTimer = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const body = await fetchCurrent();
      setState({
        loading: false,
        hasActiveRun: body.has_active_run,
        job: body.job,
        error: null,
      });
    } catch (e) {
      setState((prev) => ({ ...prev, loading: false, error: e.message || String(e) }));
    }
  }, []);

  // Initial load + polling lifecycle
  useEffect(() => {
    refresh();
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [refresh]);

  useEffect(() => {
    if (state.hasActiveRun) {
      if (!pollTimer.current) {
        pollTimer.current = setInterval(refresh, POLL_INTERVAL_MS);
      }
    } else {
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    }
  }, [state.hasActiveRun, refresh]);

  const handleStart = async (retryOf) => {
    try {
      const body = await startRun(retryOf);
      // Either started or in_progress — both are fine. Refresh to attach.
      await refresh();
      return body;
    } catch (e) {
      setState((prev) => ({ ...prev, error: e.message || String(e) }));
      throw e;
    }
  };

  const handleCancel = async () => {
    try {
      await cancelRun();
      await refresh();
    } catch (e) {
      // A 409 means the run finished on its own before the cancel landed —
      // refresh to show the final state instead of surfacing an error.
      if (e.status === 409) {
        await refresh();
        return;
      }
      setState((prev) => ({ ...prev, error: e.message || String(e) }));
    }
  };

  const handlePublish = async () => {
    try {
      await publishRun(state.job.job_id);
      await refresh();
    } catch (e) {
      // 409: a run or another publish grabbed the lock first — just show it.
      if (e.status === 409) {
        await refresh();
        return;
      }
      setState((prev) => ({ ...prev, error: e.message || String(e) }));
      throw e;
    }
  };

  const period = previousMonthClientSide();

  return (
    <Layout>
      <div className="bfr-page">
        <div className="bfr-header">
          <div>
            <h1>Basecamp Franchise Reporting</h1>
            <div className="bfr-header-period">Period: {period.month_name} {period.year}</div>
          </div>
          {!state.hasActiveRun && (
            <StartButton period={period} onStart={() => handleStart()} disabled={state.loading} />
          )}
        </div>

        {state.error && <div className="bfr-error-banner">{state.error}</div>}

        {state.loading && <div className="bfr-loading">Loading…</div>}

        {!state.loading && !state.hasActiveRun && !state.job && (
          <div className="bfr-empty-state">
            <h2>No runs yet</h2>
            <p>
              Click "Run Now" to read the configured Google Sheet and start downloading
              and uploading PDFs to Google Drive.
            </p>
          </div>
        )}

        {state.job && (
          <ActiveRunPanel
            job={state.job}
            onCancel={handleCancel}
            onRetry={state.hasActiveRun ? null : () => handleStart(state.job.job_id)}
            onPublish={state.hasActiveRun ? null : handlePublish}
          />
        )}

        {/* Past runs (excluding the currently-displayed active/most-recent job) */}
        <HistoryList
          excludeJobId={state.job ? state.job.job_id : null}
          refreshKey={state.job && state.job.job_id ? state.job.job_id : "none"}
        />
      </div>
    </Layout>
  );
}
